<template>
  <div class="import-submit-step">
    <Loader
      v-if="submitting"
      style="z-index: 55000;"
    />

    <div
      v-else
      class="submit-container p-4"
    >
      <div class="mb-4">
        <h3>{{ t('import.preview.title') }}</h3>
        <p class="text-sm text-color-secondary">
          {{ t('import.preview.subtitle') }}
        </p>
      </div>

      <div class="file-info-card p-4 mb-4 border-1 surface-border border-round">
        <div class="flex align-items-center gap-3">
          <div class="file-icon-wrapper">
            <i
              :class="[getImportTypeIcon, 'text-4xl text-green-600']"
            />
          </div>
          <div class="flex-1">
            <div class="font-semibold text-lg mb-1">
              {{ fileName }}
            </div>
            <div class="flex align-items-center gap-2 text-sm text-color-secondary">
              <i class="fas fa-database text-xs" />
              <span>{{ isItemSelectorImport ? itemsCountLabel : t('import.preview.rowsToImport', { count: totalRows }) }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Consent Section (only for contact import with missing consent) -->
      <div
        v-if="showEmailConsentQuestion || showSmsConsentQuestion || showWhatsappConsentQuestion"
        class="consent-section mb-4"
      >
        <div class="consent-header">
          <i class="fas fa-shield-check" />
          <span>{{ t('import.preview.consent.title') }}</span>
        </div>

        <div class="consent-options">
          <!-- Email consent -->
          <div
            v-if="showEmailConsentQuestion"
            class="consent-option"
            :class="{ 'is-active': consentAll?.email }"
            @click="updateConsent('email', !consentAll?.email)"
          >
            <div class="consent-option-icon">
              <i class="fas fa-envelope" />
            </div>
            <div class="consent-option-content">
              <span class="consent-option-label">{{ t('import.preview.consent.emailLabel') }}</span>
              <span class="consent-option-status">
                {{ consentAll?.email ? t('import.preview.consent.optedIn') : t('import.preview.consent.notOptedIn') }}
              </span>
            </div>
            <div class="consent-option-toggle">
              <i :class="consentAll?.email ? 'fas fa-toggle-on' : 'fas fa-toggle-off'" />
            </div>
          </div>

          <!-- SMS consent -->
          <div
            v-if="showSmsConsentQuestion"
            class="consent-option"
            :class="{ 'is-active': consentAll?.sms }"
            @click="updateConsent('sms', !consentAll?.sms)"
          >
            <div class="consent-option-icon">
              <i class="fas fa-comment-sms" />
            </div>
            <div class="consent-option-content">
              <span class="consent-option-label">{{ t('import.preview.consent.smsLabel') }}</span>
              <span class="consent-option-status">
                {{ consentAll?.sms ? t('import.preview.consent.optedIn') : t('import.preview.consent.notOptedIn') }}
              </span>
            </div>
            <div class="consent-option-toggle">
              <i :class="consentAll?.sms ? 'fas fa-toggle-on' : 'fas fa-toggle-off'" />
            </div>
          </div>

          <!-- WhatsApp consent -->
          <div
            v-if="showWhatsappConsentQuestion"
            class="consent-option"
            :class="{ 'is-active': consentAll?.whatsapp }"
            @click="updateConsent('whatsapp', !consentAll?.whatsapp)"
          >
            <div class="consent-option-icon">
              <i class="fab fa-whatsapp" />
            </div>
            <div class="consent-option-content">
              <span class="consent-option-label">{{ t('import.preview.consent.whatsappLabel') }}</span>
              <span class="consent-option-status">
                {{ consentAll?.whatsapp ? t('import.preview.consent.optedIn') : t('import.preview.consent.notOptedIn') }}
              </span>
            </div>
            <div class="consent-option-toggle">
              <i :class="consentAll?.whatsapp ? 'fas fa-toggle-on' : 'fas fa-toggle-off'" />
            </div>
          </div>
        </div>

        <p
          class="consent-hint"
          :class="{ 'is-enabled': hasAnyConsentEnabled }"
        >
          <i class="fas fa-info-circle" />
          {{ hasAnyConsentEnabled ? t('import.preview.consent.hintEnabled') : t('import.preview.consent.hintDisabled') }}
        </p>
      </div>

      <!-- Selected Items List (for item selector imports) -->
      <div
        v-if="isItemSelectorImport"
        class="items-card p-4 border-1 surface-border border-round"
      >
        <div class="flex align-items-center gap-2 mb-4">
          <i
            :class="[getImportTypeIcon, 'text-xl text-primary']"
          />
          <h4 class="text-lg font-semibold m-0">
            {{ t('import.preview.selectedItemsSummary') }}
          </h4>
        </div>

        <div class="items-list-preview">
          <div
            v-for="item in filteredSelectedItems"
            :key="item.id"
            class="preview-item-card"
          >
            <!-- Previews strip -->
            <div
              v-if="item.previews && item.previews.length > 0"
              class="preview-item-card__images"
            >
              <div
                v-for="(src, pIdx) in item.previews.slice(0, 3)"
                :key="pIdx"
                class="preview-item-card__thumb"
                :class="{ 'preview-item-card__thumb--stacked': pIdx > 0 }"
                :style="{ zIndex: 3 - pIdx }"
              >
                <img
                  :src="src"
                  :alt="`${item.name} - ${pIdx + 1}`"
                >
              </div>
              <div
                v-if="item.previews.length > 3"
                class="preview-item-card__more"
              >
                +{{ item.previews.length - 3 }}
              </div>
            </div>
            <div
              v-else
              class="preview-item-card__icon"
            >
              <i :class="[getImportTypeIcon]" />
            </div>

            <!-- Item info -->
            <div class="preview-item-card__body">
              <span class="preview-item-card__name">{{ item.name }}</span>
              <span
                v-if="item.previews && item.previews.length > 1"
                class="preview-item-card__meta"
              >
                {{ item.previews.length }} {{ t('import.preview.pages') }}
              </span>
            </div>
            <i class="fas fa-check-circle text-green-500" />
          </div>
        </div>
      </div>

      <!-- Mapping Summary (for contact/custom_data imports) -->
      <div
        v-else
        class="mapping-card p-4 border-1 surface-border border-round"
      >
        <div class="flex align-items-center gap-2 mb-4">
          <i class="fas fa-sitemap text-xl text-primary" />
          <h4 class="text-lg font-semibold m-0">
            {{ t('import.preview.mappingSummary') }}
          </h4>
        </div>

        <!-- VAPID Key Info -->
        <div
          v-if="vapidKeyLabel"
          class="vapid-key-info mb-3 p-3 border-1 border-round"
        >
          <div class="flex align-items-center gap-2">
            <i class="fas fa-key text-green-500" />
            <span class="font-medium">{{ t('import.preview.vapidKey') }}</span>
            <span class="text-primary font-semibold">{{ vapidKeyLabel }}</span>
          </div>
        </div>

        <div
          v-if="mapping.length > 0"
          class="mapping-list"
        >
          <div
            v-for="item in mapping"
            :key="item.source"
            class="mapping-item p-3 mb-2 border-1 surface-border border-round"
          >
            <div class="flex align-items-center justify-content-between">
              <div class="flex align-items-center gap-3 flex-1">
                <div class="source-field">
                  <div class="field-label text-xs text-color-secondary mb-1">
                    {{ t('import.preview.sourceColumn') }}
                  </div>
                  <div class="flex align-items-center gap-2">
                    <i class="fas fa-table text-sm text-green-500" />
                    <span class="font-medium">{{ item.source }}</span>
                  </div>
                </div>

                <div class="arrow-icon">
                  <i class="fas fa-arrow-right text-color-secondary" />
                </div>

                <div class="target-field">
                  <div class="field-label text-xs text-color-secondary mb-1">
                    {{ importType === 'contact' ? t('import.preview.targetProperty') : t('import.preview.targetField') }}
                  </div>
                  <div class="flex align-items-center gap-2">
                    <i class="fas fa-tag text-sm text-primary" />
                    <span class="font-medium text-primary">{{ item.targetLabel || getTargetLabel(item.target) }}</span>
                  </div>
                </div>
              </div>

              <div
                v-if="item.required"
                class="required-badge"
              >
                <i class="fas fa-star-fill text-xs text-red-500" />
                <span class="text-xs text-red-500 ml-1">{{ t('import.preview.required') }}</span>
              </div>
            </div>
          </div>
        </div>

        <div
          v-else
          class="text-center p-4 text-color-secondary"
        >
          <i class="fas fa-info-circle text-2xl mb-2" />
          <p class="m-0">
            {{ t('import.preview.noMapping') }}
          </p>
        </div>
      </div>

      <div
        v-if="!hideNavigation"
        class="flex justify-end gap-2 mt-4"
      >
        <Button
          :label="t('import.preview.back')"
          severity="secondary"
          outlined
          icon="fas fa-arrow-left"
          @click="$emit('back')"
        />
        <Button
          :label="t('import.preview.confirm')"
          icon="fas fa-upload"
          @click="$emit('submit')"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import {
  defineComponent, PropType, computed,
} from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'primevue/button';
import Loader from '@/components/layout/Loader.vue';
import type { ImportFieldMapping, ConsentAllOptions, SelectableItem } from '@/composables/import/types';

export default defineComponent({
  name: 'ImportPreview',
  components: {
    Button,
    Loader,
  },

  props: {
    fileName: {
      type: String,
      default: '',
    },

    totalRows: {
      type: Number,
      required: true,
    },

    mapping: {
      type: Array as PropType<ImportFieldMapping[]>,
      required: true,
    },

    submitting: {
      type: Boolean,
      default: false,
    },

    hideNavigation: {
      type: Boolean,
      default: false,
    },

    /** Import type (contact, custom_data, etc.) */
    importType: {
      type: String,
      default: '',
    },

    /** Consent options (v-model) */
    consentAll: {
      type: Object as PropType<ConsentAllOptions>,
      default: () => ({}),
    },

    /** Selected items for item selector imports (automation/bulk/template) */
    selectedItems: {
      type: Array as PropType<SelectableItem[]>,
      default: () => [],
    },

    /** Selected VAPID key label (for push notification import) */
    vapidKeyLabel: {
      type: String,
      default: '',
    },
  },

  emits: ['submit', 'back', 'update:consentAll'],

  setup(props, { emit }) {
    const { t, te } = useI18n();

    // Check if email is mapped but email_consent is not
    const showEmailConsentQuestion = computed(() => {
      if (props.importType !== 'contact') return false;

      const mappedTargets = props.mapping.map((m) => m.target);
      const hasEmail = mappedTargets.includes('email');
      const hasEmailConsent = mappedTargets.includes('email_consent');

      return hasEmail && !hasEmailConsent;
    });

    // Check if phone_number is mapped but sms_consent is not
    const showSmsConsentQuestion = computed(() => {
      if (props.importType !== 'contact') return false;

      const mappedTargets = props.mapping.map((m) => m.target);
      const hasPhone = mappedTargets.includes('phone_number');
      const hasSmsConsent = mappedTargets.includes('sms_consent');

      return hasPhone && !hasSmsConsent;
    });

    // Check if phone_number is mapped but whatsapp_consent is not
    const showWhatsappConsentQuestion = computed(() => {
      if (props.importType !== 'contact') return false;

      const mappedTargets = props.mapping.map((m) => m.target);
      const hasPhone = mappedTargets.includes('phone_number');
      const hasWhatsappConsent = mappedTargets.includes('whatsapp_consent');

      return hasPhone && !hasWhatsappConsent;
    });

    // Check if any consent is enabled
    const hasAnyConsentEnabled = computed(() => props.consentAll?.email || props.consentAll?.sms || props.consentAll?.whatsapp);

    // Check if this is an item selector import (automation/bulk/template)
    const isItemSelectorImport = computed(() => ['campaign_automation', 'campaign_bulk', 'template', 'stats_report'].includes(props.importType));

    // Get the label for items count based on import type
    const itemsCountLabel = computed(() => {
      const count = props.selectedItems.filter((item) => item.selected).length;
      switch (props.importType) {
        case 'campaign_automation':
          return t('import.preview.itemsToImport.automation', { count });
        case 'campaign_bulk':
          return t('import.preview.itemsToImport.bulk', { count });
        case 'template':
          return t('import.preview.itemsToImport.template', { count });
        case 'stats_report':
          return t('import.preview.itemsToImport.stats_report', { count });
        default:
          return t('import.preview.rowsToImport', { count });
      }
    });

    // Get icon for import type
    const getImportTypeIcon = computed(() => {
      switch (props.importType) {
        case 'campaign_automation':
          return 'fas fa-robot';
        case 'campaign_bulk':
          return 'fas fa-paper-plane';
        case 'template':
          return 'fas fa-palette';
        default:
          return 'fas fa-file';
      }
    });

    // Get only selected items for display
    const filteredSelectedItems = computed(() => props.selectedItems.filter((item) => item.selected));

    // Update consent value directly
    const updateConsent = (type: 'email' | 'sms' | 'whatsapp', value: boolean) => {
      const newConsent = { ...props.consentAll, [type]: value };
      emit('update:consentAll', newConsent);
    };

    // Get translated label for target field
    const getTargetLabel = (target: string): string => {
      if (props.importType === 'contact') {
        const translationKey = `import.contact.fields.${target}.label`;
        // Check if translation exists before using it
        if (te(translationKey)) {
          return t(translationKey);
        }
      }
      // Fallback to raw value
      return target;
    };

    return {
      t,
      showEmailConsentQuestion,
      showSmsConsentQuestion,
      showWhatsappConsentQuestion,
      hasAnyConsentEnabled,
      isItemSelectorImport,
      itemsCountLabel,
      getImportTypeIcon,
      filteredSelectedItems,
      updateConsent,
      getTargetLabel,
    };
  },
});
</script>

<style scoped>
.import-submit-step {
  min-height: 400px;
}

.submit-container {
  max-width: 900px;
  margin: 0 auto;
}

.file-info-card {
  background: linear-gradient(135deg, var(--surface-50) 0%, var(--surface-0) 100%);
  transition: all 0.3s ease;
}

.file-info-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.file-icon-wrapper {
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--green-50);
  border-radius: 12px;
}

.mapping-card {
  background-color: var(--surface-0);
}

.vapid-key-info {
  background-color: var(--green-50);
  border-color: var(--green-200) !important;
}

.mapping-list {
  max-height: 350px;
  overflow-y: auto;
  padding-right: 8px;
}

.mapping-list::-webkit-scrollbar {
  width: 6px;
}

.mapping-list::-webkit-scrollbar-track {
  background: var(--surface-100);
  border-radius: 3px;
}

.mapping-list::-webkit-scrollbar-thumb {
  background: var(--surface-400);
  border-radius: 3px;
}

.mapping-list::-webkit-scrollbar-thumb:hover {
  background: var(--surface-500);
}

.mapping-item {
  background-color: var(--surface-50);
  transition: all 0.2s ease;
}

.mapping-item:hover {
  background-color: var(--surface-100);
  transform: translateX(4px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.source-field,
.target-field {
  min-width: 150px;
}

.field-label {
  text-transform: uppercase;
  font-weight: 600;
  letter-spacing: 0.5px;
}

.arrow-icon {
  padding: 0 16px;
}

.required-badge {
  display: flex;
  align-items: center;
  padding: 4px 12px;
  background-color: var(--red-50);
  border-radius: 12px;
  white-space: nowrap;
}

/* Consent Section - Compact design */
.consent-section {
  background-color: var(--surface-50);
  border: 1px solid var(--surface-200);
  border-radius: 8px;
  overflow: hidden;
}

.consent-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background-color: var(--surface-100);
  font-weight: 600;
  font-size: 0.9rem;
  color: var(--text-color);
  border-bottom: 1px solid var(--surface-200);
}

.consent-header i {
  color: var(--primary-color);
}

.consent-options {
  display: flex;
  gap: 0.5rem;
  padding: 0.75rem;
}

.consent-option {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  background-color: var(--surface-0);
  border: 1px solid var(--surface-300);
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.consent-option:hover {
  border-color: var(--primary-300);
  background-color: var(--primary-50);
}

.consent-option.is-active {
  border-color: var(--green-400);
  background-color: var(--green-50);
}

.consent-option-icon {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--surface-100);
  border-radius: 6px;
  color: var(--text-color-secondary);
  font-size: 0.9rem;
}

.consent-option.is-active .consent-option-icon {
  background-color: var(--green-100);
  color: var(--green-600);
}

.consent-option-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
}

.consent-option-label {
  font-weight: 500;
  font-size: 0.875rem;
  color: var(--text-color);
}

.consent-option-status {
  font-size: 0.75rem;
  color: var(--text-color-secondary);
}

.consent-option.is-active .consent-option-status {
  color: var(--green-600);
}

.consent-option-toggle i {
  font-size: 1.5rem;
  color: var(--surface-400);
  transition: color 0.2s ease;
}

.consent-option.is-active .consent-option-toggle i {
  color: var(--green-500);
}

.consent-hint {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  margin: 0;
  padding: 0.75rem 1rem;
  font-size: 0.8rem;
  line-height: 1.4;
  color: var(--orange-700);
  background-color: var(--orange-50);
  border-top: 1px solid var(--orange-200);
}

.consent-hint i {
  color: var(--orange-500);
  font-size: 0.75rem;
  margin-top: 0.15rem;
}

.consent-hint.is-enabled {
  color: var(--green-700);
  background-color: var(--green-50);
  border-top-color: var(--green-200);
}

.consent-hint.is-enabled i {
  color: var(--green-500);
}

/* Items list preview (for item selector imports) */
.items-list-preview {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  max-height: 350px;
  overflow-y: auto;
}

.preview-item-card {
  display: flex;
  align-items: center;
  gap: 0.875rem;
  padding: 0.75rem 1rem;
  background-color: var(--surface-50);
  border: 1px solid var(--surface-200);
  border-radius: 8px;
  transition: all 0.2s ease;
}

.preview-item-card:hover {
  background-color: var(--surface-100);
}

.preview-item-card__images {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  position: relative;
  width: 72px;
  height: 48px;
}

.preview-item-card__thumb {
  position: absolute;
  width: 48px;
  height: 48px;
  border-radius: 6px;
  overflow: hidden;
  border: 2px solid #ffffff;
  background-color: var(--surface-100);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.preview-item-card__thumb:first-child {
  left: 0;
}

.preview-item-card__thumb--stacked:nth-child(2) {
  left: 14px;
}

.preview-item-card__thumb--stacked:nth-child(3) {
  left: 28px;
}

.preview-item-card__thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.preview-item-card__more {
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  font-size: 0.6875rem;
  font-weight: 600;
  color: var(--text-color-secondary);
  background: var(--surface-200);
  padding: 0.125rem 0.375rem;
  border-radius: 4px;
}

.preview-item-card__icon {
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--primary-50);
  color: var(--primary-400);
  border-radius: 8px;
  font-size: 1.125rem;
}

.preview-item-card__body {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
}

.preview-item-card__name {
  font-weight: 500;
  font-size: 0.9375rem;
  color: var(--text-color);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.preview-item-card__meta {
  font-size: 0.75rem;
  color: var(--text-color-secondary);
}
</style>
